import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { EvidencePanel } from '../EvidencePanel'
import { putTags, type Incident } from '../api'

/**
 * Step 6 · Evidence and tags.
 *
 * ## Why this step can exist at all
 *
 * Because the draft is a real server row with an id. Evidence attaches to an incident, and
 * an incident that only exists in browser state has nothing to attach to — which is the
 * reason the wizard creates the DRAFT at step 1 rather than POSTing everything at the end.
 *
 * The step is skippable. A report with no evidence is still a report; it simply stays
 * `UNVERIFIED` until somebody corroborates it, and the desk is built to hold exactly that
 * distinction.
 */
export function StepEvidence({
  incident, onChanged,
}: {
  incident: Incident
  onChanged: () => void | Promise<void>
}) {
  return (
    <div className="space-y-5">
      <p className="text-erp-sm text-muted-foreground">
        Optional, and skippable. Anything attached here is kept even if the report is later
        withdrawn or found unsubstantiated — evidence is append-only.
      </p>

      <TagEditor incidentId={incident.id} tags={incident.tags} onChanged={onChanged} />

      <div>
        <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
          Evidence
        </div>
        <EvidencePanel incidentId={incident.id} onChanged={onChanged} />
      </div>
    </div>
  )
}

/**
 * Tags, as a replace-the-whole-set PUT.
 *
 * The server's route is `PUT /incidents/{id}/tags` and it replaces. Modelling it here as
 * add/remove-one would need two round trips per edit and would invent an ordering the
 * server does not have.
 */
function TagEditor({
  incidentId, tags, onChanged,
}: {
  incidentId: string
  tags: string[]
  onChanged: () => void | Promise<void>
}) {
  const [draft, setDraft] = useState('')
  const [current, setCurrent] = useState<string[]>(tags)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // The parent reloads the incident after every change, so `tags` is the truth. Listing it
  // keeps this in step when evidence or another tab changes the row underneath.
  useEffect(() => { setCurrent(tags) }, [tags])

  const commit = async (next: string[]) => {
    setBusy(true)
    setError(null)
    const previous = current
    setCurrent(next)          // optimistic, so typing stays responsive on a slow link
    try {
      await putTags(incidentId, next)
      await onChanged()
    } catch (err) {
      setCurrent(previous)    // the server refused; show what it actually holds
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const add = () => {
    const tag = draft.trim()
    if (!tag || current.includes(tag)) { setDraft(''); return }
    setDraft('')
    void commit([...current, tag])
  }

  return (
    <div>
      <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        Tags
      </div>
      <ErrorStrip message={error} className="rounded-md border mb-2" />
      <div className="flex flex-wrap items-center gap-2">
        {current.map((tag) => (
          <span key={tag}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-erp-xs">
            {tag}
            <button type="button" aria-label={`Remove ${tag}`} disabled={busy}
              onClick={() => void commit(current.filter((t) => t !== tag))}
              className="opacity-60 hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          className="erp-input h-9 w-56"
          value={draft}
          disabled={busy}
          placeholder="Add a tag and press Enter"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          onBlur={add}
        />
      </div>
      <p className="mt-1 text-erp-xs text-muted-foreground/80">
        Tags are what the analytics chip cloud counts. Reuse an existing one where it fits —
        a tag used once tells nobody anything.
      </p>
    </div>
  )
}
